import { useState } from "react";
import {
  Box,
  Group,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
  useComputedColorScheme,
} from "@mantine/core";
import { IconCheck, IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import Markdown from "../components/Markdown";
import classes from "./AssistantMessage.module.css";

const toolLabels = {
  get_help: "assistant.tools.get_help",
  get_capabilities: "assistant.tools.get_capabilities",
  search_products: "assistant.tools.search_products",
  get_product_details: "assistant.tools.get_product_details",
  get_schema_overview: "assistant.tools.get_schema_overview",
  get_channel_readiness: "assistant.tools.get_channel_readiness",
};

function ToolCallProgress({ toolStatus, isStreaming }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (!toolStatus?.length) return null;

  const allDone = toolStatus.every((ts) => ts.status === "done");
  const collapsed = allDone && !isStreaming && !expanded;

  if (collapsed) {
    return (
      <UnstyledButton onClick={() => setExpanded(true)}>
        <Group gap={4}>
          <IconChevronRight size={12} color="var(--mantine-color-dimmed)" />
          <Text size="xs" c="dimmed">
            {t("assistant.usedTools", { count: toolStatus.length })}
          </Text>
        </Group>
      </UnstyledButton>
    );
  }

  return (
    <Stack gap={2}>
      {allDone && !isStreaming && (
        <UnstyledButton onClick={() => setExpanded(false)}>
          <Group gap={4}>
            <IconChevronDown size={12} color="var(--mantine-color-dimmed)" />
            <Text size="xs" c="dimmed">
              {t("assistant.hideDetails")}
            </Text>
          </Group>
        </UnstyledButton>
      )}
      {toolStatus.map((ts, i) => (
        <Group key={i} gap={6} wrap="nowrap">
          {ts.status === "running" ? (
            <Box className={classes.pulsingDot} />
          ) : (
            <IconCheck size={12} color="var(--mantine-color-facetflux-5)" />
          )}
          <Text size="xs" c={ts.status === "running" ? undefined : "dimmed"}>
            {toolLabels[ts.tool] ? t(toolLabels[ts.tool]) : ts.tool}
          </Text>
        </Group>
      ))}
    </Stack>
  );
}

function AgentAvatar() {
  return (
    <ThemeIcon color="facetflux" radius="xl" size={28} style={{ flexShrink: 0 }}>
      <Text fz={10} fw={700} c="white" lh={1}>
        AI
      </Text>
    </ThemeIcon>
  );
}

export default function AssistantMessage({ message }) {
  const isUser = message.role === "user";
  const colorScheme = useComputedColorScheme();
  const isLight = colorScheme === "light";

  if (isUser) {
    return (
      <Paper
        className={classes.userBubble}
        p="xs"
        bg={isLight ? "facetflux.0" : "facetflux.9"}
        style={{ alignSelf: "flex-end", maxWidth: "85%" }}
      >
        <Text size="sm" c={isLight ? "facetflux.9" : "facetflux.1"}>
          {message.content}
        </Text>
      </Paper>
    );
  }

  return (
    <Group align="flex-start" gap="xs" wrap="nowrap" style={{ maxWidth: "95%" }}>
      <AgentAvatar />
      <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
        <ToolCallProgress
          toolStatus={message.toolStatus}
          isStreaming={message.isStreaming}
        />
        {message.content && (
          <Paper
            className={classes.agentBubble}
            p="xs"
            bg="var(--mantine-color-default)"
            style={{ overflow: "hidden" }}
          >
            <Text size="sm" component="div">
              <div className={classes.assistantMarkdown}>
                <Markdown>{message.content}</Markdown>
              </div>
            </Text>
          </Paper>
        )}
      </Stack>
    </Group>
  );
}
